import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import styles from "./ExtractDataModal.module.css";
import sampleRateContractPDF from "../../../assets/files/Sample_rate_contract.pdf";
import axios from "axios";
import { API_ENDPOINT } from "../../../services/api/resources";
import { useSelector } from "react-redux";

const RcExtractPage = ({ onBack, onSave }) => {
  const fileInputRef = useRef(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [, setSaveRc] = useState(null);
  const data = useSelector((state) => state.loginData.value);
  const [auth_token] = useState(data?.auth_token);
  const [buyer_token] = useState(data?.user?.token);

  const handleRcDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (
      file.type !== "application/pdf" &&
      !file.name.toLowerCase().endsWith(".pdf")
    )
      return;
    setPdfFile(file);
    setExtractedData(null);
    setShowPdfPreview(false);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (
      file.type !== "application/pdf" &&
      !file.name.toLowerCase().endsWith(".pdf")
    )
      return;
    setPdfFile(file);
    setExtractedData(null);
    setShowPdfPreview(false);
  };

  const handleExtract = async () => {
    if (!pdfFile) return;
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("extract", pdfFile);

      const response = await axios.post(
        API_ENDPOINT.extract_pdf_no_filter_view,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      setExtractedData(response.data);
    } catch (error) {
      console.error("Error extracting item catalog:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("buyer_token", buyer_token);
      formData.append("extracted_response", JSON.stringify(extractedData || {}));
      formData.append("no_filter", "true");

      // Send the original rate contract PDF as binary on the requested key
      if (pdfFile) {
        formData.append("reconstructed_pdf_path", pdfFile);
      }

      const res = await axios.post(API_ENDPOINT.save_extracted_rate_contract, formData, {
        headers: {
          Authorization: `Token ${auth_token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("SAVE SUCCESS:", res.data);

      onSave(res.data);
      setSaveRc(res.data);
    } catch (err) {
      console.error("SAVE ERROR:", err);
      alert("Failed to save Rate Contract");
    } finally {
      setLoading(false);
    }
  };

  const handleClearFile = () => {
    setPdfFile(null);
    setExtractedData(null);
    setShowPdfPreview(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <HeaderTitle titleText="Extract Data" />
        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
          onClick={onBack}
        >
          Back
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 4,
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            flex: "1 1 420px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            // variant="h5"
            sx={{
              fontWeight: 600,
              color: "#1e293b",
              mb: 1,
              fontSize: "18px",
              fontFamily: "gilroyMedium",
            }}
          >
            Rate Contract
          </Typography>

          <Box
            onDrop={handleRcDrop}
            onDragOver={handleDragOver}
            sx={{
              width: "500px",
              border: "2px dashed #cbd5e1",
              borderRadius: "12px",
              p: 4,
              textAlign: "center",
              backgroundColor: "#f8fafc",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f1f5f9" },
              transition: "background-color 0.2s ease",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: "#334155",
                mb: 0.5,
                wordBreak: "break-all",
              }}
            >
              {pdfFile ? pdfFile.name : "Drag and drop file here"}
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: "#64748b", fontSize: 14, mb: 2 }}
            >
              (Supported format: .pdf)
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <Button
                variant="outlined"
                size="medium"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 14,
                  fontWeight: 500,
                  px: 2.5,
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                Browse
              </Button>

              {pdfFile && (
                <>
                  <Button
                    variant="text"
                    color="primary"
                    size="medium"
                    sx={{
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 500,
                      px: 2.5,
                    }}
                    onClick={() => setShowPdfPreview((prev) => !prev)}
                  >
                    {showPdfPreview ? "Hide Preview" : "Preview"}
                  </Button>

                  <Button
                    variant="text"
                    color="error"
                    size="medium"
                    sx={{
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 500,
                      px: 2.5,
                    }}
                    onClick={handleClearFile}
                  >
                    Clear
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {sampleRateContractPDF && (
            <a
              href={sampleRateContractPDF}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#0000ffac",
                fontSize: "1.4rem",
                marginTop: "1rem",
                cursor: "pointer",
                fontFamily: "gilroyMedium",
                textDecoration: "underline",
                display: "inline-block",
              }}
            >
              Sample Rate Contract PDF
            </a>
          )}
        </Box>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        className={styles.hiddenInput}
        onChange={handleFileChange}
      />

      {showPdfPreview && pdfFile && (
        <Box
          sx={{
            mt: 2,
            p: 1,
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            backgroundColor: "white",
            maxWidth: "720px",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "#075b97",
              fontWeight: 600,
              mb: 1,
              fontSize: 14,
              textAlign: "left",
            }}
          >
            PDF Preview:
          </Typography>
          <iframe
            src={URL.createObjectURL(pdfFile)}
            title="PDF Preview"
            width="100%"
            height="250px"
            style={{
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            // fontWeight: 600,
            fontFamily: "gilroyBold",
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            opacity: !pdfFile || loading ? 0.7 : 1,
          }}
          onClick={handleExtract}
          disabled={!pdfFile || loading}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : "Extract"}
        </Button>

        <Button
          variant="contained"
          size="small"
          sx={{
            fontFamily: "gilroyBold",
            fontSize: 15,
            // fontWeight: 600,
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            opacity: !extractedData || loading ? 0.7 : 1,
          }}
          onClick={handleSave}
          disabled={!extractedData || loading}
        >
          Save
        </Button>
      </Box>

      {extractedData && (
        <Box sx={{ mt: 4 }}>
          <Box
            sx={{
              mt: 2,
              mb: 2,
              border: "1px solid #e2e8f0",
              borderRadius: 1.5,
              p: 2,
              maxWidth: "720px",
            }}
          >
            <Typography
              sx={{ fontFamily: "gilroyMedium", fontSize: "16px", mb: 0.5 }}
            >
              <strong>RC Number:</strong>{" "}
              {extractedData?.header_info?.purchase_order_no || "-"}
            </Typography>
            <Typography
              sx={{ fontFamily: "gilroyMedium", fontSize: "16px", mb: 0.5 }}
            >
              <strong>GST-IN:</strong>{" "}
              {extractedData?.header_info?.gstin || "-"}
            </Typography>
            <Typography
              sx={{ fontFamily: "gilroyMedium", fontSize: "16px", mb: 0.5 }}
            >
              <strong>Supplier Name:</strong>{" "}
              {extractedData?.header_info?.vendor_details?.split("\n")?.[0] ||
                "-"}
            </Typography>
            <Typography sx={{ fontFamily: "gilroyMedium", fontSize: "16px" }}>
              <strong>Created Date:</strong> {extractedData?.created_at || "-"}
            </Typography>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    // "Sr. No.",
                    "Part No",
                    "Description of Goods",
                    "HSN Code",
                    "Quantity.",
                    "Unit of Measure",
                    "Rate",
                    "CGST Rate",
                    "SGST Rate",
                    "IGST Rate",
                    "Amount",
                  ].map((header, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        fontWeight: 600,
                        fontFamily: "GilroySemiBold",
                        backgroundColor: "#e0e9ee",
                        fontSize: "14px",
                        borderBottom: "1px solid #e2e8f0",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {extractedData.data.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: "white",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    {/* <TableCell sx={ { fontSize: "14px", fontFamily: "GilroyMedium" } }>{ item["Sr. No."] }</TableCell> */}
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["Part No"]}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["Description of Goods"]}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["HSN Code"]}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["Qty."]}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["UOM"]}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      ₹{Number(item["Rate"]).toLocaleString("en-IN")}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["CGST Rate"]}%
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["SGST Rate"]}%
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "14px", fontFamily: "GilroyMedium" }}
                    >
                      {item["IGST Rate"]}%
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#075b97",
                        fontWeight: 600,
                        fontSize: "14px",
                        fontFamily: "GilroyMedium",
                      }}
                    >
                      ₹{Number(item["Amount"]).toLocaleString("en-IN")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mt: 2,
            }}
          >
            {/* Totals */}
            <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, p: 2 }}>
              <Typography sx={{ fontSize: "16px", fontFamily: "GilroyMedium" }}>
                <strong>Total:</strong> ₹
                {Number(extractedData.totals.total).toLocaleString("en-IN")}
              </Typography>
              <Typography sx={{ fontSize: "16px", fontFamily: "GilroyMedium" }}>
                <strong>Taxes Total:</strong> ₹
                {Number(extractedData.totals.taxes_total).toLocaleString(
                  "en-IN",
                )}
              </Typography>
              <Typography
                sx={{
                  fontSize: "20px",
                  color: "#075b97",
                  fontFamily: "GilroyBold",
                }}
              >
                <strong>Grand Total:</strong> ₹
                {Number(extractedData.totals.grand_total).toLocaleString(
                  "en-IN",
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RcExtractPage;
